import React, { useMemo } from 'react';
import { Button, Spin, Table, Tooltip } from 'antd';
import { Link } from 'react-router-dom';
import moment from 'moment';
import {
	formatDuration,
	countCompletedWorkOrders,
	partitionWorkOrders,
	parseLocationEvents,
} from '../../utils/WrenchModeEventUtils';
import { workOrderSearchAndAPIAdaptor } from '../../thunks/work_orders_thunks_wrapper';

interface FlattenedRow {
	key: string;
	scheduleId: number;
	date: string;
	supplierEmail: string;
	locationName: string;
	locationId: number;
	workOrdersCompleted: number;
	workOrdersTotal: number;
	workOrders: any[];
	driveTimeMs: number | null;
	workTimeMs: number | null;
}

interface WrenchModeSchedulesTableProps {
	schedules: any[];
	loading: boolean;
	loadingMore: boolean;
	hasMore: boolean;
	onLoadMore: () => void;
	highlightedDate: string | null;
	onRowClick: (date: string, locationId: number) => void;
	locationIds: number[] | undefined;
}

const computeRowSpanMap = (rowList: FlattenedRow[]): Record<string, number> => {
	const spanMap: Record<string, number> = {};

	for (let groupStart = 0; groupStart < rowList.length; ) {
		const currentDate = rowList[groupStart].date;
		let groupEnd = groupStart + 1;

		while (groupEnd < rowList.length && rowList[groupEnd].date === currentDate) {
			groupEnd++;
		}

		spanMap[rowList[groupStart].key] = groupEnd - groupStart;

		for (let inner = groupStart + 1; inner < groupEnd; inner++) {
			spanMap[rowList[inner].key] = 0;
		}

		groupStart = groupEnd;
	}

	return spanMap;
};

const WrenchModeSchedulesTable: React.FC<WrenchModeSchedulesTableProps> = ({
	schedules,
	loading,
	loadingMore,
	hasMore,
	onLoadMore,
	highlightedDate,
	onRowClick,
	locationIds,
}) => {
	const { rows, rowSpanMap } = useMemo(() => {
		const hasLocationFilter = Boolean(locationIds && locationIds.length);
		const flatRows: FlattenedRow[] = [];

		for (const schedule of schedules) {
			const locations = schedule.locations || [];

			if (locations.length === 0) {
				if (!hasLocationFilter) {
					flatRows.push({
						key: schedule.id + '-0',
						scheduleId: schedule.id,
						date: schedule.date,
						supplierEmail: schedule.supplierEmail,
						locationName: 'No location',
						locationId: 0,
						workOrdersCompleted: 0,
						workOrdersTotal: 0,
						workOrders: [],
						driveTimeMs: null,
						workTimeMs: null,
					});
				}
			} else {
				for (let locIndex = 0; locIndex < locations.length; locIndex++) {
					const location = locations[locIndex];

					if (hasLocationFilter && locationIds.indexOf(location.locationId) === -1) continue;

					const locationName = (location.location && location.location.name) || 'Unknown Location';
					const workOrderIds = location.workOrderIds || [];
					const adaptedWorkOrders = workOrderSearchAndAPIAdaptor(
						(location.workOrders || []).map((workOrder) => ({ source: workOrder }))
					);

					const locationEvents = location.events || [];
					let driveTimeMs: number | null = null;
					let workTimeMs: number | null = null;

					if (locationEvents.length > 0) {
						const parsed = parseLocationEvents(locationEvents);
						driveTimeMs = parsed.driveTimeMs;
						workTimeMs = parsed.totalWorkMs;
					}

					flatRows.push({
						key: schedule.id + '-' + locIndex,
						scheduleId: schedule.id,
						date: schedule.date,
						supplierEmail: schedule.supplierEmail,
						locationName,
						locationId: location.locationId,
						workOrdersCompleted: countCompletedWorkOrders(
							adaptedWorkOrders,
							schedule.supplierEmail,
							schedule.date
						),
						workOrdersTotal: workOrderIds.length,
						workOrders: adaptedWorkOrders,
						driveTimeMs,
						workTimeMs,
					});
				}
			}
		}

		return { rows: flatRows, rowSpanMap: computeRowSpanMap(flatRows) };
	}, [schedules, locationIds]);

	const hasLocationFilter = Boolean(locationIds && locationIds.length);
	const emptyText = hasLocationFilter
		? 'No logs match the selected locations for this date range'
		: 'No work logs found for the selected date range';

	const columns = [
		{
			title: 'Date',
			dataIndex: 'date',
			key: 'date',
			render: (date: string, record: FlattenedRow) => ({
				children: moment(date).format('ddd, MMM D'),
				props: { rowSpan: rowSpanMap[record.key] },
			}),
		},
		{
			title: 'Location',
			dataIndex: 'locationName',
			key: 'locationName',
		},
		{
			title: 'Work Orders',
			key: 'workOrders',
			render: (_: any, record: FlattenedRow) => {
				const label = record.workOrdersCompleted + '/' + record.workOrdersTotal;
				if (record.workOrders.length === 0) return label;

				const { notStarted, started, ended } = partitionWorkOrders(
					record.workOrders,
					record.supplierEmail,
					record.date
				);

				const renderWoLinks = (workOrders: any[]) =>
					workOrders.map((wo, index) => (
						<span key={wo.id}>
							{index > 0 && ', '}
							<Link
								to={'/supplier/workOrders/detail/' + wo.id + '/details'}
								target="_blank"
								className="bodyregularunderline text-white"
							>
								WO #{wo.id}
							</Link>
						</span>
					));

				const content = (
					<div className="flex flex-col gap-xs">
						{notStarted.length > 0 && (
							<div className="bodyregular text-white">Not Started: {renderWoLinks(notStarted)}</div>
						)}
						{started.length > 0 && (
							<div className="bodyregular text-white">Started: {renderWoLinks(started)}</div>
						)}
						{ended.length > 0 && (
							<div className="bodyregular text-white">Ended: {renderWoLinks(ended)}</div>
						)}
					</div>
				);

				return (
					<Tooltip title={content} trigger="hover" placement="top">
						<span className="cursor-pointer underline">{label}</span>
					</Tooltip>
				);
			},
		},
		{
			title: 'Drive Time',
			key: 'driveTime',
			render: (_: any, record: FlattenedRow) => {
				if (record.driveTimeMs === null) return '--';
				return formatDuration(record.driveTimeMs);
			},
		},
		{
			title: 'Work Time',
			key: 'workTime',
			render: (_: any, record: FlattenedRow) => {
				if (record.workTimeMs === null) return '--';
				return formatDuration(record.workTimeMs);
			},
		},
	];

	const renderFooter =
		hasMore || loadingMore
			? () => (
					<div className="flex justify-center">
						{loadingMore ? (
							<Spin />
						) : (
							<Button onClick={onLoadMore} size="small">
								Load More
							</Button>
						)}
					</div>
			  )
			: undefined;

	return (
		<Table
			className="wrenchModeTable"
			columns={columns}
			dataSource={rows}
			pagination={false}
			loading={loading}
			rowClassName={(record: FlattenedRow) => {
				if (highlightedDate && record.date === highlightedDate) {
					return 'wrench-mode-row-highlighted';
				}
				return '';
			}}
			onRow={(record: FlattenedRow) => ({
				onClick: () => onRowClick(record.date, record.locationId),
				style: { cursor: 'pointer' },
			})}
			size="middle"
			locale={{ emptyText: loading ? ' ' : emptyText }}
			footer={renderFooter}
		/>
	);
};

export default WrenchModeSchedulesTable;
